import React from 'react';
import { Box, IconButton, SvgIcon, Typography } from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import {
  siDiscord,
  siFacebook,
  siInstagram,
  siKick,
  siTiktok,
  siX
} from 'simple-icons';

// Arena palette — mirrors the dashboard cards (.dash-panel / .dash-phead) so this
// block reads as part of the same system instead of a stray teal panel.
const C = {
  cyan: '#38e8ff',
  text: '#e8f4fb',
  dim: '#8ea3b3',
  lineSoft: 'rgba(140, 170, 190, 0.09)',
  mono: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace'
};

const socials = [
  { label: 'X', href: 'https://x.com/incryptofficial', path: siX.path, color: '#ffffff' },
  { label: 'Facebook', href: 'https://www.facebook.com/incryptcrypto/', path: siFacebook.path, color: '#1877f2' },
  { label: 'TikTok', href: 'https://www.tiktok.com/@incryptofficial', path: siTiktok.path, color: '#ffffff' },
  { label: 'Instagram', href: 'https://www.instagram.com/incryptau/', path: siInstagram.path, color: '#e4405f' },
  { label: 'Discord', href: 'https://discord.gg/W8FGSQgW3S', path: siDiscord.path, color: '#5865f2' },
  { label: 'Kick', href: 'https://kick.com/incryptsos', path: siKick.path, color: '#53fc18' }
];

export const DiscordInvite: React.FC = () => {
  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        borderRadius: '16px',
        p: '18px',
        border: '1px solid rgba(56, 232, 255, 0.22)',
        background:
          'linear-gradient(180deg, rgba(11, 17, 25, 0.74), rgba(7, 11, 17, 0.78))',
        boxShadow: '0 18px 40px -26px rgba(0, 0, 0, 0.9)',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: '0 auto auto 0',
          width: 260,
          height: 160,
          pointerEvents: 'none',
          background:
            'radial-gradient(360px 200px at 0 0, rgba(56, 232, 255, 0.08), transparent 65%)'
        }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          font: `800 10px ${C.mono}`,
          letterSpacing: '0.24em',
          textTransform: 'uppercase',
          color: C.dim
        }}
      >
        <PublicIcon sx={{ fontSize: 16, color: C.cyan }} /> Follow Incrypt
      </Box>

      <Typography
        sx={{
          mt: 1,
          textAlign: 'center',
          font: `600 12px ${C.mono}`,
          letterSpacing: '0.02em',
          color: C.dim
        }}
      >
        Stay in the loop with the latest Incrypt news, giveaways, videos, and community updates.
      </Typography>

      <Box
        sx={{
          mt: 2,
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(3, minmax(0, 1fr))',
            sm: 'repeat(6, minmax(0, 1fr))'
          },
          gap: 1.25
        }}
      >
        {socials.map((social) => (
          <IconButton
            key={social.label}
            component="a"
            href={social.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={social.label}
            title={social.label}
            data-track="dashboard.social"
            data-track-network={social.label}
            sx={{
              height: 52,
              borderRadius: '12px',
              color: social.color,
              background: 'rgba(255, 255, 255, 0.03)',
              border: `1px solid ${C.lineSoft}`,
              transition: 'transform 0.16s ease, border-color 0.16s ease, background 0.16s ease, box-shadow 0.16s ease',
              '&:hover': {
                background: 'rgba(56, 232, 255, 0.08)',
                borderColor: 'rgba(56, 232, 255, 0.45)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px -10px rgba(56, 232, 255, 0.5)'
              }
            }}
          >
            <SvgIcon viewBox="0 0 24 24" sx={{ fontSize: 22 }}>
              <path d={social.path} />
            </SvgIcon>
          </IconButton>
        ))}
      </Box>
    </Box>
  );
};
